import type { CSSProperties } from "react";
import { cx } from "./ui";

/*
 * Party decorations for celebration layouts (birthdays, launches, festive promos).
 * Pure markup + CSS: pieces sit scattered when motion is off (editor, thumbnails, reduced motion) and
 * fall / bob / twinkle on live sites (see `.baze-confetti-piece`, `.baze-balloon`, `.baze-twinkle` in
 * styles.css). Positions are fixed tables so renders are deterministic. Colours come from the theme.
 */

const COLORS = ["bg-site-primary", "bg-site-secondary", "bg-site-accent"] as const;
const FILLS = ["text-site-primary", "text-site-secondary", "text-site-accent"] as const;

type Var = CSSProperties & Record<`--${string}`, string>;

// [left %, top %, rotation deg, delay s, duration s, shape (0 strip, 1 dot, 2 square), colour, size px]
const PIECES: ReadonlyArray<readonly [number, number, number, number, number, 0 | 1 | 2, 0 | 1 | 2, number]> = [
  [4, 8, 20, 0, 7.5, 0, 0, 10], [11, 32, -35, 2.1, 9, 1, 2, 8], [17, 4, 60, 4.2, 8, 2, 1, 9], [23, 58, 15, 1.2, 10, 0, 2, 11],
  [29, 18, -60, 3.4, 8.5, 1, 0, 7], [35, 70, 40, 5.1, 9.5, 2, 2, 8], [41, 6, -20, 0.6, 7, 0, 1, 12], [47, 44, 75, 2.8, 10.5, 1, 0, 8],
  [53, 12, -45, 4.8, 8, 2, 2, 9], [59, 62, 30, 1.6, 9, 0, 0, 10], [65, 26, -10, 3.9, 7.5, 1, 1, 7], [71, 76, 55, 0.3, 10, 2, 0, 8],
  [77, 10, -70, 2.4, 8.5, 0, 2, 11], [83, 40, 25, 5.4, 9, 1, 1, 8], [89, 16, -30, 1, 7, 2, 0, 9], [95, 54, 65, 3.1, 10, 0, 1, 10],
  [8, 82, -15, 4.5, 9.5, 2, 1, 8], [26, 88, 35, 2.2, 8, 1, 2, 7], [62, 90, -50, 0.9, 8.5, 0, 2, 11], [92, 86, 10, 3.6, 9, 1, 0, 8],
  [14, 50, 80, 5.8, 10, 0, 1, 9], [44, 84, -25, 1.8, 7.5, 2, 0, 8], [74, 50, 45, 4, 9.5, 1, 2, 7], [86, 68, -55, 0.4, 8, 0, 0, 10],
];

/**
 * Falling confetti over the whole parent (which must be `relative` + `overflow-hidden`). The centre is
 * masked out so pieces never sit behind the heading and body text.
 */
export function Confetti({ className, sparse }: { className?: string; sparse?: boolean }) {
  const pieces = sparse ? PIECES.filter((_, i) => i % 2 === 0) : PIECES;
  return (
    <div
      aria-hidden="true"
      className={cx(
        "pointer-events-none absolute inset-0 overflow-hidden",
        "[mask-image:radial-gradient(ellipse_58%_34%_at_50%_58%,transparent_70%,#000_100%)] @3xl:[mask-image:radial-gradient(ellipse_34%_38%_at_50%_58%,transparent_70%,#000_100%)]",
        className,
      )}
    >
      {pieces.map(([left, top, rot, delay, dur, shape, color, size], i) => (
        <i
          key={i}
          className={cx("baze-confetti-piece absolute block", COLORS[color], shape === 1 ? "rounded-full" : "rounded-[2px]")}
          style={
            {
              left: `${left}%`,
              top: `${top}%`,
              width: `${shape === 0 ? Math.round(size * 0.45) : size}px`,
              height: `${shape === 0 ? size * 1.4 : size}px`,
              "--r": `${rot}deg`,
              "--d": `${delay}s`,
              "--t": `${dur}s`,
              "--x": `${(i % 2 ? -1 : 1) * (18 + (i % 5) * 6)}px`,
            } as Var
          }
        />
      ))}
    </div>
  );
}

function Balloon({ className, color, delay, style }: { className?: string; color: 0 | 1 | 2; delay: number; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 60 120" className={cx("baze-balloon absolute", FILLS[color], className)} style={{ ...style, "--d": `${delay}s` } as Var}>
      <path fill="currentColor" d="M30 2C14 2 4 15 4 31c0 18 14 33 24 37l-3 6h10l-3-6c10-4 24-19 24-37C56 15 46 2 30 2Z" />
      <ellipse cx="20" cy="22" rx="6" ry="10" fill="#fff" opacity="0.35" transform="rotate(-20 20 22)" />
      <path d="M30 74c-6 10 6 16 0 26s6 14 0 18" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    </svg>
  );
}

/** Two balloon clusters at the sides. Smaller and further out on phones so they never cover text. */
export function Balloons({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cx("pointer-events-none absolute inset-0 overflow-hidden", className)}>
      <Balloon color={0} delay={0} className="-left-3 top-[8%] w-12 @3xl:left-[4%] @3xl:w-20" />
      <Balloon color={2} delay={1.4} className="left-5 top-[24%] w-9 opacity-90 @3xl:left-[10%] @3xl:w-14" />
      <Balloon color={1} delay={0.7} className="-right-3 top-[6%] w-12 @3xl:right-[5%] @3xl:w-20" />
      <Balloon color={0} delay={2.1} className="right-6 top-[26%] w-8 opacity-90 @3xl:right-[11%] @3xl:w-12" />
    </div>
  );
}

const STAR = "M12 0c.6 5.6 3.4 9 12 12-8.6 3-11.4 6.4-12 12-.6-5.6-3.4-9-12-12C8.6 9 11.4 5.6 12 0Z";

/** Four-point sparkles that twinkle; place inside a `relative` box. */
export function Sparkles({ className }: { className?: string }) {
  const stars: Array<[string, 0 | 1 | 2, number]> = [
    ["left-[6%] top-[18%] size-5", 2, 0],
    ["right-[8%] top-[40%] size-4", 0, 0.8],
    ["left-[14%] bottom-[18%] size-3.5", 1, 1.6],
    ["right-[18%] bottom-[10%] size-5", 2, 2.3],
  ];
  return (
    <div aria-hidden="true" className={cx("pointer-events-none absolute inset-0", className)}>
      {stars.map(([pos, color, delay], i) => (
        <svg key={i} viewBox="0 0 24 24" className={cx("baze-twinkle absolute", pos, FILLS[color])} style={{ "--d": `${delay}s` } as Var}>
          <path fill="currentColor" d={STAR} />
        </svg>
      ))}
    </div>
  );
}

/** A hand-drawn squiggle under a heading; draws itself in on live sites. */
export function Squiggle({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 220 18" className={cx("baze-squiggle mx-auto block h-3.5 w-40 text-site-accent @3xl:w-56", className)}>
      <path d="M3 12c18-9 30 8 48 0s30-9 48 0 30 9 48 0 30-9 48 0 18 5 20 2" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" pathLength={1} />
    </svg>
  );
}

// [left %, top %, rotation deg, delay s, duration s, colour, size px]
const PETALS: ReadonlyArray<readonly [number, number, number, number, number, 0 | 1 | 2, number]> = [
  [6, 6, 30, 0, 13, 1, 14], [16, 30, -40, 3.5, 15, 2, 11], [27, 10, 70, 7, 14, 0, 12], [38, 52, -20, 1.8, 16, 1, 10],
  [49, 4, 50, 5.2, 13.5, 2, 13], [60, 40, -65, 9, 15.5, 1, 11], [71, 14, 15, 2.6, 14.5, 0, 14], [82, 58, -35, 6.4, 16, 2, 10],
  [92, 8, 60, 4, 13, 1, 12], [11, 72, -10, 8.2, 15, 2, 11], [44, 80, 40, 10.5, 14, 1, 13], [76, 84, -55, 0.8, 16.5, 0, 10],
];

/**
 * Soft petals drifting down (wedding covers). Same rules as confetti: static when motion is off, centre
 * masked so petals never sit behind text.
 */
export function Petals({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cx(
        "pointer-events-none absolute inset-0 overflow-hidden",
        "[mask-image:radial-gradient(ellipse_60%_30%_at_50%_55%,transparent_65%,#000_100%)] @3xl:[mask-image:radial-gradient(ellipse_32%_36%_at_50%_55%,transparent_65%,#000_100%)]",
        className,
      )}
    >
      {PETALS.map(([left, top, rot, delay, dur, color, size], i) => (
        <i
          key={i}
          className={cx("baze-petal absolute block rounded-[80%_0_80%_0]", COLORS[color])}
          style={
            {
              left: `${left}%`,
              top: `${top}%`,
              width: `${size}px`,
              height: `${Math.round(size * 0.8)}px`,
              "--r": `${rot}deg`,
              "--d": `${delay}s`,
              "--t": `${dur}s`,
              "--x": `${(i % 2 ? -1 : 1) * (40 + (i % 4) * 15)}px`,
            } as Var
          }
        />
      ))}
    </div>
  );
}
